import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cacheable } from '@datorama/akita';
import { mapTo, tap } from 'rxjs/operators';
import { ApiService } from '../../../shared/services/core/api.service';
import { NotificationCenterState, NotificationCenterStore } from './notification-center.store';
import { PagedResults } from '../../../shared/models/paged-result.model';
import { NotificationCenterMessageDto } from './models/notification-center-message-dto.model';
import { NotificationsCenterMappingService } from '../notification-center-mappings/notifications-center-mapping.service';
import { combineLatest, Subject } from 'rxjs';
import { NotificationManagementService } from '../notification-mangement/notification-management.service';
import { lowerCaseKeys } from '../../../shared/services/core/lowercasekeys.service';
import { NotificationCenterMessageView } from './models/notification-center-message-view.model';
import { MemberQuery } from '../../../account/member/member.query';
import { NotificationManagementQuery } from '../notification-mangement/notification-management.query';
import { getMessageLanguageForLcid } from './services/notification-center-helper.service';
import { NotificationsCenterMappingQuery } from '../notification-center-mappings/notifications-center-mapping.query';
import moment from 'moment/moment';

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  notificationReceived$ = new Subject<NotificationCenterMessageView>();
  private currentTimeout: ReturnType<typeof setInterval>;

  constructor(
    private store: NotificationCenterStore,
    private mappingService: NotificationsCenterMappingService,
    private notificationManagementService: NotificationManagementService,
    private notificationManagementQuery: NotificationManagementQuery,
    private api: ApiService,
    private memberQuery: MemberQuery,
    private mappingQuery: NotificationsCenterMappingQuery
  ) {}

  /**
   * Retrieve the first page for the notification center
   * @param pageSize Size of the page
   */
  fetchNotificationCenter(pageSize: number = 10) {
    const params = new HttpParams().set('page', '1').set('pageSize', pageSize.toString());

    const notificationRequest$ = this.api
      .getAnySingle<PagedResults<NotificationCenterMessageDto>>('notificationscenter?' + params.toString())
      .pipe(
        tap((notifications) => {
          notifications = lowerCaseKeys(notifications);
          this.updateTotal(notifications.totalCount);
          this.store.set(notifications.items);
        })
      );

    let requests$ = combineLatest([
      notificationRequest$,
      this.mappingService.fetchNotificationMappings(),
      this.notificationManagementService.fetchNotificationGroups(),
      this.getUnseenNotificationCount(),
    ]);

    this.updateAmountPerPage(pageSize);

    return cacheable(this.store, requests$).pipe(mapTo(null));
  }

  /**
   * Get the current unseen notification count for the user
   */
  getUnseenNotificationCount() {
    return this.api.getAnySingle<number>('notificationscenter/getunseencount').pipe(
      tap((count) => {
        this.updateUnseenCount(count);
      })
    );
  }

  /**
   * Retrieve a paged array for the notification center
   * @param page Page number
   * @param pageSize Size of the page
   */
  getNotificationCenterPage(page: number, pageSize: number = 10) {
    const params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());

    return this.api.getAnySingle<PagedResults<NotificationCenterMessageDto>>('notificationscenter?' + params.toString()).pipe(
      tap((notifications) => {
        if (notifications) {
          notifications = lowerCaseKeys(notifications);
          this.updateTotal(notifications.totalCount);
          this.store.upsertMany(notifications.items);
        }
      }),
      mapTo(null)
    );
  }

  /**
   * Post back to the API a list of notification ids that we have seen
   * @param notificationIds Array of notification Ids tht we have seen
   */
  postNotificationsAsRead(notificationIds: number[]) {
    return this.api
      .postWithArray<number>('notificationscenter/read', notificationIds)
      .pipe(
        tap(() => {
          let utcNow = moment.utc().toDate();
          this.store.update(notificationIds, { isRead: true, readDate: utcNow });
        }),
        mapTo(null)
      )
      .toPromise();
  }

  /**
   * Post back to the API that all notifications are seen
   */
  postAllNotificationsAsRead() {
    return this.api
      .post<number>('notificationscenter/readall', 0)
      .pipe(
        tap(() => {
          this.updateUnseenCount(0);
        }),
        mapTo(null)
      )
      .toPromise();
  }

  /**
   * Set the isOpen store property
   * @param isOpen Is the notification center component open
   */
  updateIsOpen(open: boolean) {
    this.store.updateUI({ isOpen: open } as NotificationCenterState['ui']);
  }

  /**
   * Set the currentPage store property
   * @param currentPage New current page property
   */
  updateCurrentPage(currentPage: number) {
    this.store.updateUI({ currentPage: currentPage } as NotificationCenterState['ui']);
  }

  /**
   * Set the AmountPerPage store property
   * @param pageSize New Amount Per Page property
   */
  updateAmountPerPage(pageSize: number) {
    this.store.updateUI({ amountPerPage: pageSize } as NotificationCenterState['ui']);
  }

  /**
   * After 5 seconds call mark notifications as read
   */
  startNotificationCheck() {
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
    }

    this.currentTimeout = setTimeout(() => {
      if (this.store.getValue().unseenCount) {
        this.postAllNotificationsAsRead();
      }
    }, 5000);
  }

  /**
   * Set the total property on the store, to be used for pagination
   * @param total Total notifications the user can see
   */
  private updateTotal(total: number) {
    this.store.update({
      total: total,
    });
  }

  /**
   * Go back one page and start the notification check
   */
  decrementPage() {
    const prevPage = this.store.getValue().ui.currentPage - 1;

    if (prevPage >= 1) {
      this.updateCurrentPage(prevPage);
      this.startNotificationCheck();
    }
  }

  /**
   * Go forward one page and start the notification check
   */
  incrementPage() {
    const nextPage = this.store.getValue().ui.currentPage + 1;

    if (nextPage <= this.MaxPage) {
      this.updateCurrentPage(nextPage);
      this.startNotificationCheck();
    }
  }

  /**
   * Handle receiving notifications, adding them to the store, and set the subject if required
   * @param notificationDto Notification DTO
   */
  receiveNotification(notificationDto: NotificationCenterMessageDto) {
    let notification = this.getNotificationViewFromDto(notificationDto);

    // If first notification add to store
    if (this.MaxPage == 0) {
      this.addNotification(notificationDto);
    } else {
      // Otherwise invalidate the store and get from API
      this.invalidateCache();
    }
    //Increment unseen count
    this.updateUnseenCount(this.store.getValue().unseenCount + 1);

    // Do something with the notification only if the notification center is not open and not read
    if (
      !this.store.getValue().ui.isOpen &&
      !notification.isRead &&
      this.notificationManagementQuery.loyaltyTypeIsActive(notification.loyaltyActionId)
    ) {
      this.notificationReceived$.next(notification);
    } else if (!this.store.getValue().ui.isOpen && !notification.isRead) {
      // Call the notification check to make sure the notification is marked as read
      // if the user is in the notification center
      this.startNotificationCheck();
    }
  }

  /**
   * Get View from DTO
   * @param notificationDto Notification DTO
   * @returns View object with correct message language
   */
  private getNotificationViewFromDto(notificationDto: NotificationCenterMessageDto) {
    let notification = notificationDto as unknown as NotificationCenterMessageView;

    // retrieve the linkUrl from the mappings and assign it
    let url = this.mappingQuery.getMappingFromNotifciation(notification);
    notification = Object.assign({}, notification, { linkUrl: url });

    let currentMessageLanguage = getMessageLanguageForLcid(
      notificationDto.messageLanguages,
      +this.memberQuery.getUserContext().lcid
    );

    notification.messageLanguage = currentMessageLanguage;

    return notification;
  }

  /**
   * Set unseen count property on the store
   * @param count New unseen notification count
   */
  private updateUnseenCount(count: number) {
    this.store.update({
      unseenCount: count,
    });
  }

  /**
   * Invalidate the cache of the store
   */
  private invalidateCache() {
    this.updateCurrentPage(1);
    this.store.setHasCache(false);
    this.store.remove();
  }

  /**
   * Add notification to the store and prepend
   */
  private addNotification(notification: NotificationCenterMessageDto) {
    this.store.add(notification, { prepend: true });
  }

  /**
   * Returns max page fusing stores values
   */
  private get MaxPage() {
    return Math.ceil(this.store.getValue().total / this.store.getValue().ui.amountPerPage);
  }
}
